"use client";

import { Container } from "@/components/layout/container";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { encodedUmrahParams } from "@/feature/umrah/package/search/lib/umrah-formatter-helper";
import { priceFormatter } from "@/lib/price-formatter";
import { cn } from "@/lib/utils";
import { baseApi } from "@/redux/api/base-api";
import { useAppDispatch } from "@/redux/hooks";
import { CalendarDays, Coins, MoonStar, Search, Users } from "lucide-react";
import { useRouter } from "next/navigation";
import { useMemo, type ReactNode } from "react";
import { IUmrahSearchQueryParams } from "../../types/umrah-search-type";

type UmrahSearchRouteInfoProps = {
  searchData: IUmrahSearchQueryParams | null;
};

type SummaryItem = {
  key: string;
  icon: ReactNode;
  value: string;
};

export default function UmrahSearchRouteInfo({
  searchData,
}: UmrahSearchRouteInfoProps) {
  const router = useRouter();
  const dispatch = useAppDispatch();

  const routeLabel = useMemo(() => {
    const keyword = searchData?.query?.trim();
    return keyword ? `Keyword: ${keyword}` : "All packages";
  }, [searchData]);

  const summaryItems = useMemo<SummaryItem[]>(() => {
    const durationValue = searchData?.duration?.length
      ? searchData.duration.join(", ")
      : "Any duration";
    const groupValue = searchData?.group_size?.length
      ? searchData.group_size.join(", ")
      : "Any group size";

    const hasPrice =
      typeof searchData?.min_price === "number" ||
      typeof searchData?.max_price === "number";
    const priceValue = hasPrice
      ? `${priceFormatter(searchData?.min_price ?? 0)} - ${priceFormatter(searchData?.max_price ?? 0)}`
      : "Any price";

    return [
      {
        key: "duration",
        icon: <CalendarDays className="h-3.5 w-3.5 shrink-0" />,
        value: durationValue,
      },
      {
        key: "group-size",
        icon: <Users className="h-3.5 w-3.5 shrink-0" />,
        value: groupValue,
      },
      {
        key: "price",
        icon: <Coins className="h-3.5 w-3.5 shrink-0" />,
        value: priceValue,
      },
    ];
  }, [searchData]);

  function handleSearchClick() {
    const params = encodedUmrahParams(searchData ?? {});
    router.push(params ? `/umrah?${params}` : "/umrah", { scroll: false });
  }

  return (
    <section className="sticky top-(--header-height) z-40 border-b border-border/40 bg-background/80 backdrop-blur supports-backdrop-filter:bg-background/70 shadow-md">
      <Container className="py-3">
        <div className="flex w-full flex-col gap-2.5 md:flex-row md:items-center md:justify-between">
          <div className="flex w-full min-w-0 items-stretch gap-2 md:w-auto">
            <div className="min-w-0 flex-1 md:flex-none">
              <RouteCard routeLabel={routeLabel} onClick={handleSearchClick} />
            </div>

            <Button
              type="button"
              onClick={handleSearchClick}
              className="shrink-0 md:hidden"
            >
              <Search className="h-4 w-4" />
              Search
            </Button>
          </div>

          <div className="flex w-full min-w-0 sm:gap-2  md:w-auto  md:items-center md:justify-end">
            {summaryItems.map((item) => (
              <SummaryCard
                key={item.key}
                icon={item.icon}
                value={item.value}
                onClick={handleSearchClick}
                className="min-w-[132px] flex-1 justify-start sm:flex-none"
              />
            ))}

            <Button
              type="button"
              onClick={() =>
                dispatch(baseApi.util.invalidateTags(["UMRAH_SEARCH"]))
              }
              className="hidden md:flex"
            >
              <Search className="h-4 w-4" />
              Search
            </Button>
          </div>
        </div>
      </Container>
    </section>
  );
}

function SummaryCard({
  icon,
  value,
  onClick,
  className,
}: {
  icon: ReactNode;
  value: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="ghost"
      type="button"
      onClick={onClick}
      className={cn(
        "min-w-0 border border-transparent bg-transparent px-3 hover:border-border/70 hover:bg-muted/70",
        className,
      )}
    >
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </span>
      <div className="min-w-0 text-left leading-tight">
        <p className="truncate text-sm font-semibold">{value}</p>
      </div>
    </Button>
  );
}

function RouteCard({
  routeLabel,
  onClick,
  className,
}: {
  routeLabel: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className={cn(
        "h-10 w-full min-w-0 justify-start overflow-hidden rounded-xl border border-transparent bg-transparent px-3 hover:border-border/70 hover:bg-muted/70",
        className,
      )}
    >
      <Badge>Umrah</Badge>
      <span className="inline-flex min-w-0 items-center gap-1">
        <MoonStar className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate text-sm font-medium">{routeLabel}</span>
      </span>
    </Button>
  );
}
